import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../Dashboard/components/Navbar';
import Footer from '../Dashboard/components/Footer';
import PropertyDetail from '../Properties/PropertyDetail';
import { complianceService } from '../../services/api';
import UploadModal from '../Properties/components/Certificates/UploadModal';
import CSVImportModal from '../Shared/CSVImportModal';
import AMLDashboard from '../AML';
import OrderHistory from '../CertificateOrdering/OrderHistory';
import BulkCertImportModal from './BulkCertImportModal';

interface Certificate {
    id: string;
    name: string;
    type: string;
    propertyId: string;
    propertyAddress?: string;
    issued: string;
    expires: string;
    status: string;
    storageKey: string;
    fileName: string;
}

export default function Certificates() {
    const navigate = useNavigate();
    const [certificates, setCertificates] = useState<Certificate[]>([]);
    const [loading, setLoading] = useState(true);
    const [filterType, setFilterType] = useState<string>('ALL');
    const [filterStatus, setFilterStatus] = useState<string>('ALL');
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [selectedPropertyId, setSelectedPropertyId] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<'inventory' | 'orders' | 'aml'>('inventory');

    // Add Upload Modal State
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
    const [showCertImportModal, setShowCertImportModal] = useState(false);

    useEffect(() => {
        fetchCertificates();
    }, []);

    const fetchCertificates = async () => {
        try {
            const response = await complianceService.getLandlordCertificates();
            const certsData = Array.isArray(response.data) ? response.data : (response.data?.data || []);

            console.log('[Certificates] Fetched:', certsData.length, 'certificates');
            setCertificates(certsData);
        } catch (error) {
            console.error('[Certificates] Failed to fetch:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (cert: Certificate) => {
        const confirmed = window.confirm(
            `Are you sure you want to delete this ${cert.type} certificate?\n\nThis action cannot be undone.`
        );

        if (!confirmed) return;

        try {
            setLoading(true);
            await complianceService.deleteCertificate(cert.id, cert.propertyId);
            console.log('[Certificates] Deleted:', cert.id);

            // Remove from local state
            setCertificates(prev => prev.filter(c => c.id !== cert.id));

            // Show success message (could use toast)
            alert('Certificate deleted successfully');
        } catch (error) {
            console.error('[Certificates] Delete failed:', error);
            alert('Failed to delete certificate. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const getStatusColor = (expires: string) => {
        const expiryDate = new Date(expires);
        const now = new Date();
        const daysUntilExpiry = Math.ceil((expiryDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

        if (daysUntilExpiry < 0) return 'red';
        if (daysUntilExpiry <= 30) return 'yellow';
        return 'green';
    };

    const getStatusText = (expires: string) => {
        const expiryDate = new Date(expires);
        const now = new Date();
        const daysUntilExpiry = Math.ceil((expiryDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

        if (daysUntilExpiry < 0) return `Expired ${Math.abs(daysUntilExpiry)} days ago`;
        if (daysUntilExpiry <= 30) return `Expires in ${daysUntilExpiry} days`;
        return 'Valid';
    };

    const filteredCertificates = certificates.filter(cert => {
        const typeMatch = filterType === 'ALL' || cert.type === filterType;
        const statusColor = getStatusColor(cert.expires);
        const statusMatch = filterStatus === 'ALL' ||
            (filterStatus === 'VALID' && statusColor === 'green') ||
            (filterStatus === 'EXPIRING' && statusColor === 'yellow') ||
            (filterStatus === 'EXPIRED' && statusColor === 'red');

        // Certs in this codebase have inconsistent shapes — some are missing
        // `type`, `fileName`, or `propertyAddress`. Without these guards, a
        // single legacy row crashes the whole search with "Cannot read
        // properties of undefined (reading 'toLowerCase')" and white-screens
        // the page.
        const addressRaw = typeof cert.propertyAddress === 'object'
            ? (cert.propertyAddress as any)?.line1
            : cert.propertyAddress;
        const address = (addressRaw || '').toString().toLowerCase();
        const q = searchTerm.toLowerCase();
        const searchMatch = !searchTerm ||
            (cert.type || '').toLowerCase().includes(q) ||
            address.includes(q) ||
            (cert.fileName || '').toLowerCase().includes(q);

        return typeMatch && statusMatch && searchMatch;
    });


    // Get unique certificate types for filter
    const certificateTypes = ['ALL', ...Array.from(new Set(certificates.map(c => c.type)))];

    if (loading) {
        return (
            <div className="bg-gray-50 min-h-screen flex flex-col">
                <Navbar />
                <div className="flex-1 flex justify-center items-center">
                    <div className="animate-pulse flex flex-col items-center">
                        <div className="h-12 w-12 bg-emerald-200 rounded-full mb-4"></div>
                        <div className="h-4 w-32 bg-gray-200 rounded"></div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-gray-50 min-h-screen font-sans flex flex-col">
            <Navbar />

            <div className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-8">
                {/* Header Section */}
                <div className="md:flex md:items-center md:justify-between mb-8">
                    <div className="flex-1 min-w-0">
                        <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
                            Compliance Hub
                        </h2>
                        <p className="mt-1 text-sm text-gray-500">
                            Centralized command for certificates, ordering, and AML compliance
                        </p>
                    </div>
                </div>

                {/* Tab Navigation */}
                <div className="mb-8 border-b border-gray-200">
                    <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                        <button
                            onClick={() => setActiveTab('inventory')}
                            className={`${activeTab === 'inventory' ? 'border-emerald-500 text-emerald-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
                        >
                            <i className={`fas fa-certificate mr-2 ${activeTab === 'inventory' ? 'text-emerald-500' : 'text-gray-400'}`}></i>
                            Certificate Inventory
                        </button>
                        <button
                            onClick={() => setActiveTab('orders')}
                            className={`${activeTab === 'orders' ? 'border-emerald-500 text-emerald-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
                        >
                            <i className={`fas fa-shopping-cart mr-2 ${activeTab === 'orders' ? 'text-emerald-500' : 'text-gray-400'}`}></i>
                            Order Management
                        </button>
                        <button
                            onClick={() => setActiveTab('aml')}
                            className={`${activeTab === 'aml' ? 'border-emerald-500 text-emerald-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
                        >
                            <i className={`fas fa-fingerprint mr-2 ${activeTab === 'aml' ? 'text-emerald-500' : 'text-gray-400'}`}></i>
                            AML Compliance
                        </button>
                    </nav>
                </div>

                {activeTab === 'inventory' && (
                    <>
                        <div className="md:flex md:items-center md:justify-between mb-6">
                            <div className="flex-1">
                                <h3 className="text-lg font-medium text-gray-900">Active Portfolio</h3>
                                <p className="text-sm text-gray-500">{certificates.length} total certificates managed</p>
                            </div>
                            <div className="mt-4 flex md:mt-0 md:ml-4">
                                <button
                                    type="button"
                                    onClick={() => setShowCertImportModal(true)}
                                    className="inline-flex items-center px-4 py-2 border border-emerald-600 rounded-md shadow-sm text-sm font-medium text-emerald-600 bg-white hover:bg-emerald-50 mr-2"
                                >
                                    <i className="fas fa-file-import mr-2"></i>
                                    Bulk Upload
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setIsUploadModalOpen(true)}
                                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500"
                                >
                                    <i className="fas fa-plus mr-2"></i>
                                    Add Certificate
                                </button>
                            </div>
                        </div>

                        {certificates.length === 0 ? (
                            <div className="text-center py-20 bg-white rounded-xl border border-dashed border-gray-300">
                                <div className="mx-auto h-24 w-24 bg-emerald-100 rounded-full flex items-center justify-center mb-6">
                                    <i className="fas fa-certificate text-4xl text-emerald-600"></i>
                                </div>
                                <h3 className="mt-2 text-lg font-medium text-gray-900">No certificates yet</h3>
                                <p className="mt-1 text-sm text-gray-500 max-w-md mx-auto">
                                    Get started by adding your first property or uploading compliance certificates.
                                </p>
                                <div className="mt-6 space-x-4">
                                    <button
                                        type="button"
                                        className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-emerald-700 bg-emerald-100 hover:bg-emerald-200"
                                        onClick={() => navigate('/add-property')}
                                    >
                                        <i className="fas fa-home mr-2"></i>
                                        Add New Property
                                    </button>
                                    <button
                                        type="button"
                                        className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-emerald-600 hover:bg-emerald-700"
                                        onClick={() => setIsUploadModalOpen(true)}
                                    >
                                        <i className="fas fa-cloud-upload-alt mr-2"></i>
                                        Upload to Existing Property
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <>
                                {/* Filters & Search */}
                                <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6 shadow-sm flex flex-col md:flex-row gap-4">
                                    <div className="relative flex-1">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <i className="fas fa-search text-gray-400"></i>
                                        </div>
                                        <input
                                            type="text"
                                            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 sm:text-sm"
                                            placeholder="Search by certificate type or address..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                    </div>
                                    <div className="flex gap-4">
                                        <select
                                            className="block w-40 pl-3 pr-10 py-2 border-gray-300 focus:ring-emerald-500 focus:border-emerald-500 sm:text-sm rounded-lg"
                                            value={filterType}
                                            onChange={(e) => setFilterType(e.target.value)}
                                        >
                                            {certificateTypes.map(type => (
                                                <option key={type} value={type}>
                                                    {type === 'ALL' ? 'All Types' : type}
                                                </option>
                                            ))}
                                        </select>
                                        <select
                                            className="block w-40 pl-3 pr-10 py-2 border-gray-300 focus:ring-emerald-500 focus:border-emerald-500 sm:text-sm rounded-lg"
                                            value={filterStatus}
                                            onChange={(e) => setFilterStatus(e.target.value)}
                                        >
                                            <option value="ALL">All Status</option>
                                            <option value="VALID">Valid</option>
                                            <option value="EXPIRING">Expiring Soon</option>
                                            <option value="EXPIRED">Expired</option>
                                        </select>
                                    </div>
                                </div>

                                {/* Certificates Grid */}
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {filteredCertificates.map((cert) => {
                                        const statusColor = getStatusColor(cert.expires);
                                        const statusText = getStatusText(cert.expires);
                                        const isExpiredOrExpiring = statusColor === 'red' || statusColor === 'yellow';

                                        return (
                                            <div key={cert.id} className="bg-white rounded-xl border border-slate-200 p-4 hover:shadow-md transition-all group flex flex-col justify-between h-full">
                                                <div className="flex justify-between items-start mb-3">
                                                    <div className="flex items-center gap-3">
                                                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center bg-${statusColor}-50 text-${statusColor}-600 border border-${statusColor}-100`}>
                                                            <i className="fas fa-certificate text-sm"></i>
                                                        </div>
                                                        <div>
                                                            <h4 className="text-sm font-black text-slate-900 leading-none">{cert.type.replace(/_/g, ' ').toUpperCase()}</h4>
                                                            <p className="text-[10px] text-slate-500 mt-1 truncate max-w-[150px]" title={typeof cert.propertyAddress === 'object' ? (cert.propertyAddress as any)?.line1 : cert.propertyAddress}>
                                                                {typeof cert.propertyAddress === 'object' ? (cert.propertyAddress as any)?.line1 : cert.propertyAddress || cert.fileName}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <span className={`text-[10px] font-black px-2 py-1 rounded-md bg-${statusColor}-50 text-${statusColor}-700 border border-${statusColor}-100`}>
                                                        {statusColor === 'green' ? 'VALID' : (statusColor === 'red' ? 'EXPIRED' : 'WARNING')}
                                                    </span>
                                                </div>

                                                <div className="bg-slate-50 rounded-lg p-2 mb-4">
                                                    <div className="flex justify-between items-center text-[10px] text-slate-500 uppercase font-black tracking-wider">
                                                        <span>Expiry Date</span>
                                                        <span className={`text-${statusColor}-600`}>{statusText}</span>
                                                    </div>
                                                    <div className="text-xs font-bold text-slate-800 mt-0.5">
                                                        {new Date(cert.expires).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                                                    </div>
                                                </div>

                                                <div className="flex gap-2 border-t border-slate-50 pt-3">
                                                    {isExpiredOrExpiring && (
                                                        <button
                                                            onClick={() => navigate(`/properties/${cert.propertyId}?action=replace&type=${cert.type}`)}
                                                            className="flex-1 text-[10px] font-black bg-amber-50 text-amber-600 py-2 rounded-lg hover:bg-amber-600 hover:text-white transition-all uppercase tracking-tight"
                                                        >
                                                            Replace
                                                        </button>
                                                    )}
                                                    <button
                                                        onClick={() => handleDelete(cert)}
                                                        className="h-8 w-8 flex items-center justify-center text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                                                        title="Delete"
                                                    >
                                                        <i className="fas fa-trash-alt text-xs"></i>
                                                    </button>
                                                    <button
                                                        onClick={() => setSelectedPropertyId(cert.propertyId)}
                                                        className="flex-1 text-[10px] font-black bg-emerald-50 text-emerald-600 py-2 rounded-lg hover:bg-emerald-600 hover:text-white transition-all uppercase tracking-tight"
                                                        title="View property details"
                                                    >
                                                        View Property
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>

                                {filteredCertificates.length === 0 && (
                                    <div className="text-center py-12 bg-white rounded-lg border border-gray-200 mt-4">
                                        <p className="text-gray-500">No certificates match the selected filters.</p>
                                    </div>
                                )}
                            </>
                        )}
                    </>
                )}

                {activeTab === 'orders' && (
                    <div className="space-y-6">
                        <OrderHistory />
                    </div>
                )}

                {activeTab === 'aml' && (
                    <div className="space-y-6">
                        <AMLDashboard />
                    </div>
                )}
            </div>

            <Footer />

            {/* Property Details Drawer Modal */}
            {selectedPropertyId && (
                <div
                    className="fixed inset-0 z-50 overflow-hidden bg-gray-900 bg-opacity-50"
                    onClick={(e) => {
                        if (e.target === e.currentTarget) {
                            setSelectedPropertyId(null);
                        }
                    }}
                >
                    <div className="absolute inset-0 overflow-hidden">
                        <div className="absolute inset-y-0 right-0 flex max-w-full pl-10">
                            <div className="w-screen max-w-2xl">
                                <div className="h-full overflow-y-auto bg-white shadow-xl rounded-l-2xl">
                                    <div className="relative flex flex-col h-full">
                                        <PropertyDetail
                                            propertyId={selectedPropertyId}
                                            isDrawer={true}
                                            onClose={() => setSelectedPropertyId(null)}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Global Upload Modal */}
            <UploadModal
                isOpen={isUploadModalOpen}
                onClose={() => setIsUploadModalOpen(false)}
                onSuccess={fetchCertificates}
                propertyId={undefined}
                certificateType={undefined}
            />

            <BulkCertImportModal
                isOpen={showCertImportModal}
                onClose={() => setShowCertImportModal(false)}
                onSuccess={fetchCertificates}
            />
        </div>
    );
}
